import { Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { BarChart2, ChevronDown, ChevronRight, TableIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Bar, BarChart, XAxis, YAxis } from 'recharts';

import {
  type AttendanceAnalysis,
  type DanceClass,
  useGetAttendanceAnalysis,
} from '~entities/classes/api/get-attendance-analysis';

import { DataTableColumnHeader } from '~shared/components/table/header';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '~shared/shadcn/ui/chart';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~shared/shadcn/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

const columnHelper = createColumnHelper<AttendanceAnalysis>();
const classColumnHelper = createColumnHelper<DanceClass>();

const classColumns = [
  classColumnHelper.accessor('className', {
    header: 'Class Name',
    cell: (info) => {
      const value = info.getValue();
      const classId = info.row.original.classId;
      return (
        <Button variant='link' asChild>
          <Link to='/class-analytics/$classId' params={{ classId }}>
            {value}
          </Link>
        </Button>
      );
    },
  }),
  classColumnHelper.accessor('absentCount', {
    header: 'Absentees',
  }),
];

const AttendanceTable = ({
  data,
  grouping,
}: {
  data: Array<AttendanceAnalysis>;
  grouping: 'className' | 'groupName';
}) => {
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'expander',
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <Button
              variant='ghost'
              onClick={row.getToggleExpandedHandler()}
              className='p-0 hover:bg-transparent'
            >
              {row.getIsExpanded() ? (
                <ChevronDown className='size-4' />
              ) : (
                <ChevronRight className='size-4' />
              )}
            </Button>
          ) : null;
        },
        enableHiding: true,
      }),
      columnHelper.accessor('name', {
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='Name' />
        ),
        cell: (info) => {
          const row = info.row.original;

          if (grouping === 'className') {
            const classId = row.classes[0]?.classId;
            if (classId) {
              return (
                <Button variant='link' asChild>
                  <Link to='/class-analytics/$classId' params={{ classId }}>
                    {info.getValue()}
                  </Link>
                </Button>
              );
            }
          }

          return info.getValue();
        },
      }),
      columnHelper.accessor('total', {
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='Total Classes' />
        ),
      }),
      columnHelper.accessor('absentCount', {
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='Absentees' />
        ),
      }),
      columnHelper.accessor('absentPercent', {
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='Absent %' />
        ),
        cell: (info) => {
          const value = info.getValue();
          return <div>{(value * 100).toFixed(1)}%</div>;
        },
      }),
    ],
    [grouping],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) =>
      grouping === 'groupName' && row.original.classes.length > 0,
    state: {
      columnVisibility: {
        expander: grouping === 'groupName',
      },
    },
  });

  return (
    <div className='rounded-md border'>
      <div className='max-h-[500px] overflow-auto'>
        <Table>
          <TableHeader className='sticky top-0 bg-background'>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <>
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className='min-w-[100px]'>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
                {row.getIsExpanded() && (
                  <TableRow>
                    <TableCell colSpan={columns.length} className='p-0'>
                      <div className='bg-muted/50 px-4 py-2'>
                        <div className='max-h-[300px] overflow-auto'>
                          <NestedClassesTable classes={row.original.classes} />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const NestedClassesTable = ({ classes }: { classes: Array<DanceClass> }) => {
  const table = useReactTable({
    data: classes,
    columns: classColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Table>
      <TableHeader className='sticky top-0 bg-muted/50'>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id}>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell
                key={cell.id}
                className={
                  cell.column.id === 'className'
                    ? 'min-w-[200px]'
                    : 'min-w-[100px]'
                }
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export function AttendanceChart({ sessionId }: { sessionId: string }) {
  const [showTable, setShowTable] = useState(false);
  const [grouping, setGrouping] = useState<'className' | 'groupName'>(
    'className',
  );

  const { data: attendanceData, isLoading } = useGetAttendanceAnalysis({
    groupBy: grouping,
    sessionId,
  });

  const chartData = useMemo(() => {
    if (!attendanceData) return [];
    return [...attendanceData]
      .sort((a, b) => b.absentPercent - a.absentPercent)
      .slice(0, 10);
  }, [attendanceData]);

  return (
    <Card className='col-span-1 flex h-full flex-col'>
      <CardHeader className='flex flex-col space-y-2 sm:flex-row sm:items-start sm:justify-between sm:space-y-0'>
        <div>
          <CardTitle>Class Absence Summary</CardTitle>
          <CardDescription>Class-wise absence (%)</CardDescription>
        </div>
        <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
          <Select
            value={grouping}
            onValueChange={(value: 'className' | 'groupName') => {
              setGrouping(value);
            }}
          >
            <SelectTrigger className='w-full sm:w-[180px]'>
              <SelectValue placeholder='Select grouping' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='groupName'>By Group Name</SelectItem>
              <SelectItem value='className'>By Class Name</SelectItem>
            </SelectContent>
          </Select>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='outline'
                  size='icon'
                  onClick={() => {
                    setShowTable(!showTable);
                  }}
                >
                  {showTable ? (
                    <BarChart2 className='size-4' />
                  ) : (
                    <TableIcon className='size-4' />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Switch to {showTable ? 'chart' : 'table'} view</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent className='flex-1 overflow-hidden'>
        {isLoading ? (
          <div className='flex h-[200px] items-center justify-center'>
            Loading...
          </div>
        ) : !showTable ? (
          <ChartContainer
            config={{
              absentPercent: {
                label: 'Absent %',
                color: 'hsl(var(--chart-1))',
              },
            }}
            className='h-[200px] w-full overflow-hidden sm:h-[250px] md:h-[300px]'
          >
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
              barSize={30}
            >
              <XAxis
                dataKey='name'
                axisLine={false}
                tickLine={false}
                padding={{ left: 10, right: 10 }}
                angle={-35}
                textAnchor='end'
                height={50}
                interval={0}
                tick={{ fontSize: 11 }}
                tickMargin={5}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                padding={{ top: 10 }}
                tick={{ fontSize: 12 }}
                tickFormatter={(value: number) => `${value.toFixed(1)}%`}
              />
              <ChartTooltip
                content={<ChartTooltipContent />}
                cursor={{ fill: 'transparent' }}
              />
              <Bar
                dataKey='absentPercent'
                fill='hsl(var(--chart-1))'
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ChartContainer>
        ) : (
          <div className='max-h-[calc(100vh-400px)] overflow-auto'>
            <AttendanceTable
              data={chartData}
              grouping={grouping}
              key={grouping}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
